import { getSubdomain } from "../../utils/getSubdomain";
import { routes } from "../../router/Router";
import BaseHead from "../templates/BaseHead";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Divider,
  Typography,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import { FolderOpen, ExpandMore } from "@material-ui/icons";
import { Fragment, useEffect, useState } from "react";
import { format } from "date-fns";
import axios from "axios";
import { getAuth } from "firebase/auth";
import tokenNotFound from "../../utils/api/config/tokenNotFound";
import TopBackground from "../organisms/TopBackground";

type FolderAndFileProps = {
  folderName?: string;
  files: File[];
};

const FolderAndFileList = ({ folderName, files }: FolderAndFileProps) => {
  const classes = useStyles();

  const getDownload = async (fileId: number) => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    if (!token) return tokenNotFound;
    try {
      const url = `${process.env.REACT_APP_API_ENDPOINT}/uploads/download`;
      const { data } = await axios.post<DownloadResponse>(
        url,
        {
          uploads_file_id: fileId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const link = document.createElement("a");
      link.href = data.data.url;
      link.click();
    } catch (e) {
      console.error(e);
    }
  };
  return (
    <>
      <CustomAccordion defaultExpanded={!folderName}>
        <CustomAccordionSummery
          expandIcon={<ExpandMore style={{ color: "#0E0E0E" }} />}
        >
          <Box display="flex" gridGap={8} alignItems="center">
            <FolderOpen style={{ color: "#0E0E0E" }} />
            <Typography className={classes.folderName}>
              {folderName ? folderName : "その他のファイル"}
            </Typography>
          </Box>
        </CustomAccordionSummery>
        {files
          .sort((a, b) => (a.modified > b.modified ? -1 : 1))
          .map((file) => {
            return (
              <FileDetails
                key={file.id}
                file={file}
                getDownload={getDownload}
              />
            );
          })}
      </CustomAccordion>
    </>
  );
};
type FileDetailsProps = {
  file: File;
  getDownload: (fileId: number) => void;
};

const FileDetails = ({ file, getDownload }: FileDetailsProps) => {
  const classes = useStyles();

  return (
    <Fragment>
      <CustomAccordionDetails>
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => getDownload(file.id)}
        >
          <Box minWidth={100}>
            <Typography noWrap className={classes.fileName}>
              {file.name}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.fileDateTitle}>更新日：</Typography>
            <Typography className={classes.fileDate}>
              {format(new Date(file.modified), "yyyy/MM/dd")}
            </Typography>
          </Box>
        </Box>
      </CustomAccordionDetails>
      <Divider className={classes.divider} />
    </Fragment>
  );
};
const CustomAccordion = withStyles({
  root: {
    "&$expanded": {
      margin: 0,
    },
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
  expanded: {},
})(Accordion);

const CustomAccordionSummery = withStyles((theme) => ({
  root: {
    marginBottom: -1,
    minHeight: 58,
    "&$expanded": {
      minHeight: 58,
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 32,
      "&$expanded": {
        minHeight: 32,
      },
    },
  },
  content: {
    "&$expanded": {
      margin: "12px 0",
    },
  },
  expanded: {},
}))(AccordionSummary);

const CustomAccordionDetails = withStyles((theme) => ({
  root: {
    cursor: "pointer",
    padding: "16px 24px",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 24px",
    },
  },
}))(AccordionDetails);

const useStyles = makeStyles((theme) => ({
  card: {
    width: "100%",
    maxWidth: 992,
    border: "1px solid #C7C7C7",
    borderRadius: "16px",
    boxShadow: "none",
    background: "transparent",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: 700,
    padding: "16px 8px",
    fontSize: 20,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
  folderName: {
    fontSize: 18,
    fontWeight: 700,
    color: "#0E0E0E",
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
    },
  },
  fileName: {
    fontWeight: 400,
    fontSize: 16,
  },
  fileDate: {
    fontSize: 16,
    fontWeight: 400,
    minWidth: 70,
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
  },
  fileDateTitle: {
    fontSize: 12,
    fontWeight: 400,
    minWidth: 70,
    [theme.breakpoints.down("sm")]: {
      fontSize: 8,
    },
  },
  divider: {
    "&:last-child": {
      display: "none",
      backgroundColor: "#D3D3D3",
    },
  },
}));

type File = {
  id: number;
  uploads_folder_id: number;
  name: string;
  file_type: string;
  file_size: number;
  updated_by: string;
  modified: string;
};

type UploadResponse = {
  status: string;
  data: {
    uid: string;
    member_id: number;
    folders: {
      id: number;
      parent_folder_id: number;
      name: string;
      updated_by: string;
      modified: string;
      files: File[];
    }[];
    root_files: File[];
  };
};

type DownloadResponse = {
  status: string;
  data: {
    url: string;
  };
};

export const FilesPage: React.FC = () => {
  const classes = useStyles();
  const subdomain = getSubdomain(window.location.hostname);
  const [uploadsResponse, setUploadsResponse] = useState<UploadResponse>();

  const getUploads = async () => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    if (!token) return tokenNotFound;
    try {
      const url = `${process.env.REACT_APP_API_ENDPOINT}/uploads/list`;
      const { data } = await axios.get<UploadResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUploadsResponse(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    getUploads();
  }, []);

  return (
    <BaseHead
      title={routes.mypage.title}
      login
      subdomain={subdomain}
      hasBackground
    >
      <TopBackground />
      <Box display="flex" justifyContent="center" mt={4} mx={2}>
        <Card className={classes.card}>
          <Typography className={classes.cardHeader}>ファイル</Typography>
          {uploadsResponse && (
            <>
              {uploadsResponse.data.folders
                .sort((a, b) => (a.modified > b.modified ? -1 : 1))
                .map((folder) => (
                  <FolderAndFileList
                    key={folder.id}
                    folderName={folder.name}
                    files={folder.files}
                  />
                ))}
              {<FolderAndFileList files={uploadsResponse.data.root_files} />}
            </>
          )}
        </Card>
      </Box>
    </BaseHead>
  );
};
